import { supabase } from './supabaseClient';

// Клиент cortivox-api.
//
// API отдаётся с того же origin, что и сайт, под /api — как /auth и /rest у
// Supabase: в dev его проксирует src/setupProxy.js на локальный uvicorn, в
// проде — nginx контейнера. Поэтому базового URL нет и настраивать нечего:
// раньше здесь был фолбэк на http://localhost:3001, и он попал в прод-бандл.

/** Ошибка API: message — текст для пользователя, status/code — для логики. */
export class ApiError extends Error {
  constructor(message, { status, code } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

async function getAccessToken() {
  if (!supabase) throw new ApiError('Авторизация не настроена в этой сборке.');
  const { data, error } = await supabase.auth.getSession();
  if (error || !data?.session?.access_token) {
    throw new ApiError('Не удалось получить токен авторизации. Войдите заново.');
  }
  return data.session.access_token;
}

async function request(path, { method, body, fallbackError, auth = true } = {}) {
  const headers = {};
  if (auth) headers.Authorization = `Bearer ${await getAccessToken()}`;
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  const init = { headers };
  if (method) init.method = method;
  if (body !== undefined) init.body = JSON.stringify(body);

  const response = await fetch(`/api${path}`, init);
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new ApiError(payload?.error || `${fallbackError} (код ${response.status})`, {
      status: response.status,
      code: payload?.code,
    });
  }
  return payload;
}

/** Создаёт профиль текущего пользователя. Один раз, сразу после signUp. */
export const createProfile = ({ role, full_name, specialization, birth_date, education_years }) =>
  request('/profiles', {
    method: 'POST',
    body: { role, full_name, specialization, birth_date, education_years },
    fallbackError: 'Ошибка создания профиля',
  });

/** Профиль текущего пользователя. 404 (code: profile_not_found) — профиля ещё нет. */
export const getMyProfile = () => request('/profiles/me', { fallbackError: 'Не удалось получить профиль' });

/**
 * Все связи текущего пользователя (врач↔пациент), в любом статусе.
 * У каждой связи есть `counterpart` — профиль второй стороны, чтобы в
 * интерфейсе показывать имя, а не uuid.
 */
export const getLinks = () => request('/links', { fallbackError: 'Не удалось получить список связей' });

/** Поиск второй стороны по точному email (для привязки пациента). */
export const lookupProfile = (email) =>
  request(`/profiles/lookup?email=${encodeURIComponent(email.trim())}`, {
    fallbackError: 'Не удалось найти пользователя',
  });

/** Запрос на связь: роли (кто врач, кто пациент) сервер определяет по профилям. */
export const createLink = (counterpartId) =>
  request('/links', { method: 'POST', body: { counterpartId }, fallbackError: 'Не удалось отправить запрос' });

/** Принять/отклонить запрос может только вторая сторона, отозвать — любая. */
const transition = (action, fallbackError) => (linkId) =>
  request(`/links/${linkId}/${action}`, { method: 'PATCH', fallbackError });

export const acceptLink = transition('accept', 'Не удалось принять запрос');
export const rejectLink = transition('reject', 'Не удалось отклонить запрос');
export const revokeLink = transition('revoke', 'Не удалось отозвать связь');

/**
 * Сегодняшняя дата пациента (локальная, YYYY-MM-DD). Ориентировка в шкалах
 * проверяется по ней. Не toISOString(): та даёт дату по UTC, а в Москве с 00:00
 * до 03:00 это ещё вчера.
 */
export function localToday() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Расчёт шкалы на сервере. Публичный — без входа и без токена: ничего не
 * сохраняет, а результат гостю доступен (решение в PLAN.md).
 * → { scoreResult, algorithmVersion, isPendingManualReview }
 */
export const scoreScale = (scaleId, { rawAnswers, manualScores, patientProfile, manualItemIds, today }) =>
  request(`/scales/${scaleId}/score`, {
    method: 'POST',
    auth: false,
    body: { rawAnswers, manualScores, patientProfile, manualItemIds, today },
    fallbackError: 'Ошибка расчёта шкалы',
  });

/**
 * Сохранить отчёт за пациентом (только врач с активной связью). Балл считает
 * сервер — по профилю пациента в БД, а не по присланному.
 * → { id, scoreResult, algorithmVersion, isPendingManualReview, scoredOn }
 */
export const saveReport = (report) =>
  request('/reports', { method: 'POST', body: report, fallbackError: 'Ошибка сохранения отчёта' });

/** Оценить рисунки/ручные пункты сохранённого отчёта; сервер пересчитывает на дату теста. */
export const rateReport = (reportId, { manualScores, manualItemIds }) =>
  request(`/reports/${reportId}/manual-scores`, {
    method: 'PATCH',
    body: { manualScores, manualItemIds },
    fallbackError: 'Ошибка сохранения оценки',
  });

/**
 * Входные данные следующей CV-сессии: {userSettings, calibrationInfo}
 * (калибровка и найденные пороги из прошлых отчётов).
 */
export const getCvInputs = ({ exerciseSlug, patientId }) => {
  const params = new URLSearchParams({ exerciseSlug });
  if (patientId) params.set('patientId', patientId);
  return request(`/cv-reports/inputs?${params}`, { fallbackError: 'Не удалось получить настройки упражнения' });
};

/**
 * Ручные параметры упражнения пациента (кнопка «Параметры» у плашки):
 * {exerciseSlug, settings} для одного упражнения. patientId — врачу
 * (пациент себя видит и без него).
 */
export const getCvSettings = (exerciseSlug, patientId) => {
  const params = new URLSearchParams({ exerciseSlug });
  if (patientId) params.set('patientId', patientId);
  return request(`/cv-reports/settings?${params}`, { fallbackError: 'Не удалось получить параметры упражнения' });
};

/** Сохранить ручные параметры упражнения — полная замена набора значений. */
export const putCvSettings = (exerciseSlug, settings, patientId) => {
  const body = { exerciseSlug, settings };
  if (patientId) body.patientId = patientId;
  return request('/cv-reports/settings', {
    method: 'PUT',
    body,
    fallbackError: 'Не удалось сохранить параметры упражнения',
  });
};

/**
 * Сессии CV-упражнений, у которых есть покадровый trace, от новых к старым
 * (без самого trace). patientId не нужен пациенту; врачу — обязателен.
 */
export const listCvTraces = ({ patientId } = {}) => {
  const params = patientId ? `?${new URLSearchParams({ patientId })}` : '';
  return request(`/cv-reports/traces${params}`, { fallbackError: 'Не удалось получить список сессий' });
};

/** Одна сессия: {id, exerciseSlug, createdAt, report, trace}. */
export const getCvTrace = (reportId) =>
  request(`/cv-reports/${encodeURIComponent(reportId)}/trace`, { fallbackError: 'Не удалось загрузить сессию' });

/**
 * Видео сессии: {url, expiresIn, startMs, endMs, frames}. url подписан и живёт
 * expiresIn секунд. Нет видео — ApiError с code video_not_found (404),
 * хранилище не настроено — video_disabled (503).
 */
export const getCvVideo = (reportId) =>
  request(`/cv-reports/${encodeURIComponent(reportId)}/video`, { fallbackError: 'Не удалось получить видео сессии' });

/**
 * ИПР пациента: упражнения в порядке выполнения — {items: [{exerciseSlug, position}]}.
 * patientId не нужен пациенту; врачу (карточка пациента) — обязателен.
 */
export const getCvIpr = (patientId) => {
  const params = patientId ? `?${new URLSearchParams({ patientId })}` : '';
  return request(`/cv-reports/ipr${params}`, { fallbackError: 'Не удалось получить план реабилитации' });
};

/**
 * Сохранить ИПР целиком: slugs — список упражнений в новом порядке
 * (чекбокс «Включить в ИПР» в Каталоге добавляет/убирает, drag в ЛК меняет порядок).
 */
export const putCvIpr = (slugs, patientId) => {
  const body = { slugs };
  if (patientId) body.patientId = patientId;
  return request('/cv-reports/ipr', {
    method: 'PUT',
    body,
    fallbackError: 'Не удалось сохранить план реабилитации',
  });
};

/**
 * Отчёты ПО ШКАЛАМ пациента — список сохранённых оценок (кабинет врача,
 * вкладка «Отчёты → Шкалы»). patientId не нужен пациенту; врачу — обязателен.
 * Ожидаемый ответ сервера: { items: [...] } или массив, каждый элемент —
 * сохранённый отчёт шкалы (id, scale, scoreResult, isPendingManualReview, scoredOn…).
 * НОВЫЙ эндпоинт GET /api/reports — если сервер его ещё не отдаёт (404),
 * кабинет покажет честный пустой экран; см. ПРОМПТ-Клоду-отчёты-по-шкалам.md.
 */
export const listScaleReports = ({ patientId } = {}) => {
  const params = patientId ? `?${new URLSearchParams({ patientId })}` : '';
  return request(`/reports${params}`, { fallbackError: 'Не удалось получить отчёты по шкалам' });
};

// ─── Тренажёр «Речевая беглость» (шаг 6а) ───

/**
 * Задание тренажёра по блоку программы ('01'..'20').
 * → { stepId, questionText, config }, config.fluency: {mode, letter, category}.
 * Контент публичный, как question_bank; токен нужен только для входа.
 */
export const getTrainerSession = (stepId) =>
  request(`/trainers/session/${encodeURIComponent(stepId)}`, {
    fallbackError: 'Не удалось загрузить задание тренажёра',
  });

/**
 * Сохранить тренировку (только врач с активной связью). Слова считает
 * СЕРВЕР из транскрипта — клиентские числа не принимаются.
 * body: { patientId, stepId, transcript, today }
 * → { id, stepId, count, counted, scoredOn }; count — справочное значение, не балл.
 */
export const saveTrainerReport = (body) =>
  request('/trainers', { method: 'POST', body, fallbackError: 'Ошибка сохранения тренировки' });

// ─── Тренажёр: сессия пользователя и история (добавка к шагу 6а) ───

/**
 * UUID пользователя из сессии Supabase. Для пациента это и есть patientId
 * (тот же uid(), что в RLS-политиках). supabase — тот же клиент, что уже
 * импортирован выше в этом файле; если он у вас называется иначе — поправьте имя.
 */
export const getAuthUserId = async () => {
  const { data } = await supabase.auth.getSession();
  return data?.session?.user?.id || null;
};

/**
 * История прохождений: GET /trainers?patientId=&limit= → { items: [...] }.
 * Пациент видит свои отчёты, врач — пациента по активной связи.
 */
export const listTrainerReports = ({ patientId, limit } = {}) => {
  const params = new URLSearchParams();
  if (patientId) params.set('patientId', patientId);
  if (limit) params.set('limit', String(limit));
  const qs = params.toString();
  return request(`/trainers${qs ? `?${qs}` : ''}`, {
    fallbackError: 'Не удалось загрузить историю тренажёра',
  });
};

// ─── Тренажёр «Зрительное восприятие» (наложенные фигуры) ───

/**
 * Карточка 1..20: {card, task: 'objects'|'letters', title, questionText,
 * imageUrl, audioUrl, answers: [{answer, aliases}], config}.
 * Каждая карточка показывается 2 занятия подряд — как в «Беглости речи»:
 * занятие (день программы) N → карточка ceil(N/2): дни 1–2 → карточка 1,
 * …, дни 39–40 → карточка 20. stepId отчёта — ПО КАРТОЧКЕ: 'v01'..'v20'.
 * answers отдаётся для автофиниша на клиенте; авторитетный счёт всё равно
 * делает сервер из transcript.
 */
export const getVisualSession = (card) =>
  request(`/trainers/visual/session/${encodeURIComponent(Number(card))}`, {
    fallbackError: 'Не удалось загрузить карточку',
  });

/**
 * Сохранить тренировку (пациент сам — свой patientId, врач — с активной
 * связью). Счёт считает СЕРВЕР из transcript — клиентские числа не
 * принимаются.
 * body: { patientId, stepId: 'v01'..'v20', transcript, elapsedMs, today }
 * → { id, stepId, card, task, count, total, matched, missed, errors,
 *     elapsedMs, scoredOn }
 */
export const saveVisualReport = (body) =>
  request('/trainers/visual', { method: 'POST', body, fallbackError: 'Ошибка сохранения тренировки' });

// ─── Упражнение «Пересказ близко к тексту» (раздел «Связная речь») ───

/**
 * Активные рассказы: {items: [{code: 't01', num, author, title, blocks:
 * [{name, from, to}], coreTotal, detailTotal}]}. Формулировки вех и ключи
 * НЕ отдаются — счёт всегда делает сервер.
 */
export const getRetellStories = () =>
  request('/trainers/retell/stories', { fallbackError: 'Не удалось загрузить список рассказов' });

/**
 * Рассказ для экрана: {code, num, author, title, storyText, blocks, config:
 * {softLimitSec, hintLimit, hintCount}, milestones: [{num, level,
 * hintQuestion}]}. Врачу (по токену) дополнительно doctorDetails — «Детали
 * для врача» по номерам вех, для сверки зачёта по стенограмме.
 */
export const getRetellStory = (code) =>
  request(`/trainers/retell/story/${encodeURIComponent(String(code || '').trim())}`, {
    fallbackError: 'Не удалось загрузить рассказ',
  });

/**
 * Сохранить пересказ (пациент сам — свой patientId, врач — с активной связью).
 * Счёт считает СЕРВЕР из transcript; клиентские числа не принимаются.
 * body: { patientId, stepId: 'r01'..'r04', transcript, elapsedMs, thinkingMs,
 *         hintsUsed, today }
 * → { id, stepId, story, title, scoreResult, scoredOn }
 */
export const saveRetellReport = (body) =>
  request('/trainers/retell', { method: 'POST', body, fallbackError: 'Ошибка сохранения пересказа' });
